import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import DietaryLabel from '../DietaryLabel.jsx';

// Small thumbnail carousel for admin grid, mirrors MenuCard behavior
const ItemThumbCarousel = ({ item }) => {
  const slideImages = (() => {
    const combined = [
      ...(Array.isArray(item.imageUrls) ? item.imageUrls : []),
      item.imageUrl
    ].filter(Boolean);
    return Array.from(new Set(combined));
  })();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('next');
  const isCarousel = slideImages.length > 1;

  useEffect(() => {
    if (!isCarousel) return;
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setSlideDirection('next');
        setCurrentSlide((prev) => (prev + 1) % slideImages.length);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [isCarousel, isTransitioning, slideImages.length]);

  const nextSlide = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!isTransitioning && slideImages.length > 1) {
      setIsTransitioning(true);
      setSlideDirection('next');
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [isTransitioning, slideImages.length]);

  const prevSlide = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!isTransitioning && slideImages.length > 1) {
      setIsTransitioning(true);
      setSlideDirection('prev');
      setCurrentSlide((prev) => (prev - 1 + slideImages.length) % slideImages.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [isTransitioning, slideImages.length]);

  const goToSlide = useCallback((e, index) => {
    if (e) e.stopPropagation();
    if (!isTransitioning && index !== currentSlide) {
      setIsTransitioning(true);
      setSlideDirection(index > currentSlide ? 'next' : 'prev');
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [currentSlide, isTransitioning]);

  if (slideImages.length === 0) {
    return (
      <img
        src={'https://via.placeholder.com/300x300?text=No+Image'}
        alt={item.name || 'Menu Item'}
        className="w-full h-full object-cover"
      />
    );
  }

  return (
    <div className="relative w-full h-full">
      <div className="carousel-container w-full h-full relative overflow-hidden">
        {slideImages.map((url, index) => {
          const isActive = index === currentSlide;
          const isPrev = index === (currentSlide - 1 + slideImages.length) % slideImages.length;
          const isNext = index === (currentSlide + 1) % slideImages.length;
          return (
            <div
              key={`${item.id}-thumb-${index}`}
              className={`carousel-slide absolute inset-0 ${
                isActive ? 'active z-10' : isPrev ? 'prev z-5' : isNext ? 'next z-5' : 'hidden'
              }`}
              style={{
                animation: isActive
                  ? slideDirection === 'next'
                    ? 'slideInFromRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'slideInFromLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  : isPrev
                  ? slideDirection === 'next'
                    ? 'slideOutToLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'slideOutToRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none'
              }}
            >
              <img src={url} alt={item.name || 'Menu Item'} className="w-full h-full object-cover" />
            </div>
          );
        })}
      </div>
      {isCarousel && (
        <>
          <div className="absolute inset-0 flex items-center justify-between px-1 z-10 pointer-events-none">
            <button onClick={prevSlide} className="carousel-btn prev w-6 h-6 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:bg-primary hover:text-white transition-all duration-300 shadow pointer-events-auto">
              <i className="fas fa-chevron-left text-[10px]"></i>
            </button>
            <button onClick={nextSlide} className="carousel-btn next w-6 h-6 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:bg-primary hover:text-white transition-all duration-300 shadow pointer-events-auto">
              <i className="fas fa-chevron-right text-[10px]"></i>
            </button>
          </div>
          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-1 z-10">
            {slideImages.map((_, index) => (
              <span key={index} className={`dot w-1.5 h-1.5 bg-white/60 rounded-full cursor-pointer transition-all duration-300 ${index === currentSlide ? 'active bg-primary scale-110' : ''}`} onClick={(e) => goToSlide(e, index)}></span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ItemsGrid = ({
  items,
  loading,
  onEdit,
  onDelete
}) => {
  if (loading && items.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🍞</div>
        <p className="text-gray-500 text-lg">No items yet. Add your first bakery item!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
      {items.map((item) => (
        <div
          key={item.id}
          className="bg-white rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 overflow-hidden min-w-0 flex flex-col"
        >
          <div className="aspect-square overflow-hidden relative">
            <ItemThumbCarousel item={item} />
          </div>
          <div className="p-3 sm:p-4 md:p-6 flex flex-col flex-1">
            <h3 className="font-bold text-sm sm:text-base md:text-xl text-gray-800 mb-2">
              {item.name || 'Untitled Item'}
            </h3>
            <div className="flex flex-wrap gap-1 mb-2">
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                {item.category || 'Uncategorized'}
              </span>
              {item.dietaryPreference && (
                <DietaryLabel preference={item.dietaryPreference} size="xs" />
              )}
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {item.hasWeightOptions && item.weightOptions && item.weightOptions.length > 0 ? (
                item.weightOptions.map((pair, index) => (
                  <span
                    key={index}
                    className="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium whitespace-nowrap"
                  >
                    {pair.weight} - ₹{pair.price}
                  </span>
                ))
              ) : item.singlePrice ? ( 
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                  ₹{item.singlePrice}
                </span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                  No pricing info
                </span>
              )}
            </div>

            <div className="flex gap-1 sm:gap-2 mt-auto">
              <button
                onClick={() => onEdit(item)}
                className="flex-1 bg-amber-600 text-white px-1 sm:px-2 py-1 sm:py-1.5 rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center text-xs sm:text-sm min-w-0"
              >
                <Edit2 className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1 flex-shrink-0" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={() => onDelete(item.id)}
                className="flex-1 bg-stone-400 text-white px-1 sm:px-2 py-1 sm:py-1.5 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center text-xs sm:text-sm min-w-0"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1 flex-shrink-0" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ItemsGrid; 